import { X, Crown, Check } from "lucide-react";
import { useNavigation } from "@/contexts/NavigationContext";

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  compact?: boolean; // If true, renders as inline compact card instead of modal
}

export function UpgradeModal({ isOpen, onClose, compact = false }: UpgradeModalProps) {
  const { navigate } = useNavigation();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    onClose();
    navigate('/pricing');
  };

  // Compact inline version for agent modal - displays as popup overlay
  if (compact) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[80] p-3 sm:p-4">
        <div className="w-full max-w-[368px] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="bg-gradient-to-br from-purple-600 to-blue-600 text-white p-4 relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-white/70 hover:text-white transition-colors"
            title="Close"
            aria-label="Close"
          >
            <X className="size-5" />
          </button>
          
          <div className="flex items-center gap-2 pr-8">
            <Crown className="size-5" />
            <h3 className="font-bold text-lg">Upgrade ORA</h3>
          </div>
          <p className="text-white/90 text-xs mt-1">Unlock premium features</p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-gray-400">
          
          {/* Premium Features Section */}
          <section>
            <h4 className="font-semibold text-sm text-gray-900 mb-2 flex items-center gap-2">
              <Check className="size-4 text-green-600" />
              Premium Features
            </h4>
            <ul className="space-y-1.5 text-xs text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>Unlimited questions & AI interactions</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>AI accelerator training program</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>Analytics Dashboard access</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>Custom AI avatar creation</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>Real-time intelligence with context</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>Google Drive & SharePoint integration</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>Complete data privacy</span>
              </li>
            </ul>
          </section>

          {/* Video Production Service */}
          <section className="bg-white rounded-lg p-3 border border-gray-200">
            <h4 className="font-semibold text-sm text-gray-900 mb-1">
              Professional Content Delivery
            </h4>
            <p className="text-xs text-gray-700 font-semibold mb-2">Create & Deploy Training Videos</p>
            
            <div className="bg-white rounded p-2 mb-2 border border-gray-200">
              <div className="text-xs font-bold text-gray-900 mb-2 bg-gray-50 rounded p-1.5 text-center">
                6 Professional Videos • $2,500 (one-time)
              </div>
              <ul className="space-y-0.5 text-xs text-gray-800">
                <li className="flex items-start gap-2">
                  <span className="text-gray-900 font-bold">✓</span>
                  <span>1-3 minute videos per topic</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-900 font-bold">✓</span>
                  <span>Professional script services</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-900 font-bold">✓</span>
                  <span>Visual enhancement editing</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-900 font-bold">✓</span>
                  <span>Custom avatar integration</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-900 font-bold">✓</span>
                  <span>ORA & Social Media ready</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-900 font-bold">✓</span>
                  <span>Transcript + full support</span>
                </li>
              </ul>
            </div>
          </section>

          {/* AI Pro Level 1 - Avatar Basic Template */}
          <section className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-3 border-2 border-blue-300">
            <h4 className="font-bold text-sm text-blue-900 mb-1 flex items-center gap-2">
              <span className="text-lg">🤖</span>
              AI Pro Level 1
            </h4>
            <p className="text-xs text-blue-700 font-semibold mb-2">Avatar Basic Template</p>
            
            <div className="bg-white rounded p-2.5 border border-blue-200">
              <div className="text-sm font-bold text-blue-900 mb-2 bg-blue-50 rounded p-1.5 text-center">
                $24 <span className="text-green-600">(one time - no monthly commitment)</span>
              </div>
              <ul className="space-y-0.5 text-xs text-gray-800">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Your script (20-30 words)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>No revisions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span>Avatar Base Template becomes foundation for your persona and much more!</span>
                </li>
              </ul>
            </div>
          </section>

          {/* AI Pro Level 2 - Persona Pro Video */}
          <section className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-3 border-2 border-green-300">
            <h4 className="font-bold text-sm text-green-900 mb-1 flex items-center gap-2">
              <span className="text-lg">🎥</span>
              AI Pro Level 2
            </h4>
            <p className="text-xs text-green-600 font-semibold mb-2">Persona Pro Video Intro</p>
            
            <div className="bg-white rounded p-2.5 border border-green-200">
              <div className="text-sm font-bold text-green-900 mb-2 bg-green-50 rounded p-1.5 text-center">
                $24 <span className="text-green-600">(one time - no monthly commitment)</span>
              </div>
              <ul className="space-y-0.5 text-xs text-gray-800">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">•</span>
                  <span>30-45 second custom AI avatar creation</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">•</span>
                  <span>Based on existing avatar (ID required)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">•</span>
                  <span>Your custom script</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Pricing Plans */}
          <section>
            <h4 className="font-semibold text-sm text-gray-900 mb-2">Plans</h4>
            <div className="space-y-2">
              <div className="bg-gray-50 rounded p-2 border-l-4 border-blue-500">
                <div className="text-xs font-bold text-gray-900">Starter Avatar set up</div>
                <div className="text-xs text-gray-600">$15/month</div>
              </div>
              <div className="bg-gray-50 rounded p-2 border-l-4 border-purple-500">
                <div className="text-xs font-bold text-gray-900">Professional</div>
                <div className="text-xs text-gray-600">$99/month • Full access</div>
              </div>
              <div className="bg-gray-50 rounded p-2 border-l-4 border-green-500">
                <div className="text-xs font-bold text-gray-900">Enterprise</div>
                <div className="text-xs text-gray-600">Custom pricing • Everything</div>
              </div>
            </div>
          </section>

          {/* Benefits */}
          <section>
            <h4 className="font-semibold text-sm text-gray-900 mb-2">Why Upgrade?</h4>
            <ul className="space-y-1 text-xs text-gray-700">
              <li>🚀 Scale your AI leadership impact</li>
              <li>📊 Track measurable transformation</li>
              <li>💰 Earn app credits through training</li>
              <li>🎯 Accelerate team AI adoption</li>
              <li>🔐 Enterprise-grade security</li>
            </ul>
          </section>
        </div>

        {/* Action Buttons */}
        <div className="border-t border-gray-200 p-3 space-y-2 bg-gray-50">
          <button
            onClick={handleUpgrade}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-2 rounded-lg text-sm font-semibold hover:from-purple-700 hover:to-blue-700 transition-all shadow-md"
          >
            View All Plans
          </button>
          <button
            onClick={onClose}
            className="w-full bg-white text-gray-700 py-2 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-colors border border-gray-200"
          >
            Continue Free
          </button>
        </div>
        </div>
      </div>
    );
  }

  // Full modal version (default) - Matches RAG format
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header - RAG Style */}
        <div className="relative bg-gradient-to-br from-purple-600 via-blue-600 to-purple-700 text-white p-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
              <Crown className="w-6 h-6" />
            </div>
            <h2 className="text-2xl font-bold">Upgrade ORA</h2>
          </div>
          <p className="text-white/90 text-sm">
            Unlock premium features & accelerate your AI transformation
          </p>
        </div>

        {/* Scrollable Content - RAG Style */}
        <div className="flex-1 overflow-y-auto space-y-5 p-5 bg-white [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-gray-400">
          
          {/* Premium Features Section */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Check className="w-5 h-5 text-green-600" />
              Premium Features
            </h3>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-xs text-gray-700">
                <span className="text-green-600 mt-0.5 font-bold">✓</span>
                <span>Real-time intelligence with custom context</span>
              </li>
              <li className="flex items-start gap-2 text-xs text-gray-700">
                <span className="text-green-600 mt-0.5 font-bold">✓</span>
                <span>AI leadership video library access</span>
              </li>
              <li className="flex items-start gap-2 text-xs text-gray-700">
                <span className="text-green-600 mt-0.5 font-bold">✓</span>
                <span>Unlimited questions & AI interactions</span>
              </li>
              <li className="flex items-start gap-2 text-xs text-gray-700">
                <span className="text-green-600 mt-0.5 font-bold">✓</span>
                <span>Custom AI avatar creation</span>
              </li>
              <li className="flex items-start gap-2 text-xs text-gray-700">
                <span className="text-green-600 mt-0.5 font-bold">✓</span>
                <span>Analytics Dashboard access</span>
              </li>
              <li className="flex items-start gap-2 text-xs text-gray-700">
                <span className="text-green-600 mt-0.5 font-bold">✓</span>
                <span>Google Drive & SharePoint integration</span>
              </li>
              <li className="flex items-start gap-2 text-xs text-gray-700">
                <span className="text-green-600 mt-0.5 font-bold">✓</span>
                <span>Complete data privacy & security</span>
              </li>
            </ul>
          </section>

          {/* Feature Training Videos Section - Professional Content Delivery */}
          <section className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <h3 className="text-sm font-bold text-black mb-1 flex items-center gap-2">
              <span className="text-lg">🎬</span>
              Professional Content Delivery
            </h3>
            <p className="text-xs text-black font-semibold mb-3">Create & Deploy Professional Training Videos</p>
            
            <div className="bg-white rounded p-3 border border-gray-200">
              <div className="text-sm font-bold text-black mb-3 bg-white rounded p-2 text-center border border-gray-300">
                6 Professional Videos • $2,500 (one-time)
              </div>
              <ul className="space-y-1.5 text-xs text-black">
                <li className="flex items-start gap-2">
                  <span className="text-gray-900 font-bold">✓</span>
                  <span>1-3 minute videos per topic</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-900 font-bold">✓</span>
                  <span>Professional script services</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-900 font-bold">✓</span>
                  <span>Visual enhancement & editing</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-900 font-bold">✓</span>
                  <span>Custom avatar integration</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-900 font-bold">✓</span>
                  <span>ORA & Social Media ready</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-900 font-bold">✓</span>
                  <span>Full transcript & support</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Pricing Plans Section */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Pricing Plans</h3>
            <div className="space-y-2">
              <div className="bg-white rounded p-3 border-l-4 border-blue-500 border-t border-r border-b border-gray-200">
                <div className="text-xs font-bold text-gray-900">Starter</div>
                <div className="text-xs text-gray-600">$29/month • Core features</div>
              </div>
              <div className="bg-white rounded p-3 border-l-4 border-purple-500 border-t border-r border-b border-gray-200">
                <div className="text-xs font-bold text-gray-900">Professional</div>
                <div className="text-xs text-gray-600">$99/month • Full access</div>
              </div>
              <div className="bg-white rounded p-3 border-l-4 border-green-500 border-t border-r border-b border-gray-200">
                <div className="text-xs font-bold text-gray-900">Enterprise</div>
                <div className="text-xs text-gray-600">Custom pricing • Everything</div>
              </div>
            </div>
          </section>

          {/* Why Upgrade Section */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Why Upgrade?</h3>
            <ul className="space-y-1.5 text-xs text-gray-700">
              <li>🚀 Scale your AI leadership impact across teams</li>
              <li>📊 Track measurable transformation & ROI</li>
              <li>💡 Access advanced AI governance training</li>
              <li>🎯 Accelerate organizational AI adoption</li>
              <li>🔐 Enterprise-grade security & compliance</li>
            </ul>
          </section>
        </div>

        {/* Action Buttons Footer - RAG Style */}
        <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-2">
          <button
            onClick={handleUpgrade}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-2.5 rounded-lg text-sm font-semibold hover:from-purple-700 hover:to-blue-700 transition-all shadow-md"
          >
            View All Plans →
          </button>
          <button
            onClick={onClose}
            className="w-full bg-white text-gray-700 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-colors border border-gray-300"
          >
            Continue Free
          </button>
        </div>
      </div>
    </div>
  );
}